import React, { memo, useMemo } from 'react';
import { Placement as Nav4ProjectsSection } from '@post-office/placements/nav-4-projects-section';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils/src/utils/component-with-condition/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { PostOfficeProvider } from '@atlassian/jira-post-office-provider/src/index.tsx';
import { PlacementFetchProvider } from '@atlassian/jira-post-office-provider/src/placement-fetch-provider.tsx';
import { usePlacementPreloadedData } from '@atlassian/jira-post-office-provider/src/preload-placement.tsx';
import { postOfficeNav4ProjectsSectionResource } from '@atlassian/jira-router-resources-post-office/src/services/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment';
import type { DefaultMessagesResponse } from '@post-office/placement-common-v2/types';

type Props = {
	onMessageCountChange?: (numberOfMessages: number) => void;
};

export const Nav4ProjectsSectionPlacementOld = memo(({ onMessageCountChange }: Props) => {
	// Atlaspack couldn't bundle Post Office placement, so we need to return null here
	// https://atlassian.slack.com/archives/C0288U06PGC/p1755502530582239
	if (__SERVER__) {
		return null;
	}

	return (
		<UFOSegment name="jira-growth-nav-4-projects-section">
			<JSErrorBoundary
				id="growthNav4ProjectsSection"
				packageName="@atlassian/jira-growth-nav-4-projects-section"
				teamName="Sequoia"
				fallback="unmount"
			>
				<PostOfficeProvider>
					<Nav4ProjectsSection onMessageCountChange={onMessageCountChange} />
				</PostOfficeProvider>
			</JSErrorBoundary>
		</UFOSegment>
	);
});

export const Nav4ProjectsSectionPlacementNew = memo(({ onMessageCountChange }: Props) => {
	const [preloadedInitialData] = usePlacementPreloadedData(postOfficeNav4ProjectsSectionResource);
	const memoizedPlacementProps = useMemo(
		() => ({
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			preloadedInitialData: preloadedInitialData as DefaultMessagesResponse | undefined,
		}),
		[preloadedInitialData],
	);

	return (
		<UFOSegment name="jira-growth-nav-4-projects-section">
			<JSErrorBoundary
				id="growthNav4ProjectsSection"
				packageName="@atlassian/jira-growth-nav-4-projects-section"
				teamName="Sequoia"
				fallback="unmount"
			>
				<PostOfficeProvider>
					<PlacementFetchProvider resource={postOfficeNav4ProjectsSectionResource}>
						<Nav4ProjectsSection
							{...memoizedPlacementProps}
							onMessageCountChange={onMessageCountChange}
						/>
					</PlacementFetchProvider>
				</PostOfficeProvider>
			</JSErrorBoundary>
		</UFOSegment>
	);
});

export const Nav4ProjectsSectionPlacement = componentWithCondition(
	() => fg('enable_ssr_recommendations_in_nav4'),
	Nav4ProjectsSectionPlacementNew,
	Nav4ProjectsSectionPlacementOld,
);
